/**
 * FileMakerScriptConfig - FileMaker Script source configuration
 * 
 * Executes a FileMaker script and extracts data from the result
 * 
 * CHANGE HISTORY:
 * - 251201_0335 danielbeer: Created FileMaker Script configuration UI
 */

import React, { useState } from 'react';
import { JsonEditor } from './JsonEditor';
import type { FileMakerScriptConfig as ScriptConfigType, ScriptPattern } from '../../types/FileMakerTypes';
import { DEFAULT_FM_SCRIPT_CONFIG } from '../../types/FileMakerTypes';
import './DataSourceConfig.css';

export interface FileMakerScriptConfigProps {
  config: ScriptConfigType;
  onChange: (config: Partial<ScriptConfigType>) => void;
  onTest?: () => Promise<void>;
}

export function FileMakerScriptConfig({
  config,
  onChange,
  onTest
}: FileMakerScriptConfigProps) {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    data?: any;
  } | null>(null);
  const [selectedPattern, setSelectedPattern] = useState<ScriptPattern>('custom');

  const applyPattern = (pattern: ScriptPattern) => {
    switch (pattern) {
      case 'get-schema':
        onChange({
          parameters: {
            action: 'GetSchema',
            fileName: '' // Optional - leave empty for all files
          },
          resultPath: '$.JSON'
        });
        break;
      
      case 'sql-query':
        onChange({
          parameters: {
            action: 'ExecuteSQL',
            query: 'SELECT * FROM YourTable WHERE Status = ?',
            params: ['Active']
          },
          resultPath: '$.JSON'
        });
        break;
      
      case 'table-data':
        onChange({
          parameters: {
            action: 'GetTableData',
            table: 'YourTable',
            limit: 100
          },
          resultPath: '$.JSON'
        });
        break;
      
      case 'field-list':
        onChange({
          parameters: {
            action: 'GetFields',
            table: 'YourTable'
          },
          resultPath: '$.JSON'
        });
        break;
      
      case 'custom':
      default:
        // Keep current config
        break;
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await onTest?.();
      setTestResult({
        success: true,
        message: 'Script executed successfully',
        data: { sample: 'result' }
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Script execution failed'
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="filemaker-script-config">
      <div className="config-section">
        <h4>Script Settings</h4>
        
        <div className="form-group">
          <label>Script Name</label>
          <input
            type="text"
            value={config.scriptName || DEFAULT_FM_SCRIPT_CONFIG.scriptName || ''}
            onChange={(e) => onChange({ scriptName: e.target.value })}
            placeholder="jsRunScript"
          />
          <span className="form-hint">
            Name of the FileMaker script to execute. <strong>Default: jsRunScript</strong>
          </span>
        </div>

        <div className="form-group">
          <label>Script Pattern</label>
          <select
            value={selectedPattern}
            onChange={(e) => {
              const pattern = e.target.value as ScriptPattern;
              setSelectedPattern(pattern);
              applyPattern(pattern);
            }}
          >
            <option value="custom">Custom Script</option>
            <option value="get-schema">📋 Get Schema (Files/Tables/Fields)</option>
            <option value="sql-query">🔍 FileMaker SQL Query</option>
            <option value="table-data">📊 Get Table Data</option>
            <option value="field-list">📝 Get Field List</option>
          </select>
          <span className="form-hint">
            Choose a pattern to auto-configure parameters for common tasks
          </span>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={config.webViewerContext || false}
              onChange={(e) => onChange({ webViewerContext: e.target.checked })}
            />
            Execute from WebViewer context
          </label>
          <span className="form-hint">
            If checked, script runs in WebViewer context (for this tool running in FM WebViewer)
          </span>
        </div>
      </div>

      <div className="config-section">
        <h4>Script Parameters (JSON)</h4>
        
        <div className="form-group">
          <JsonEditor
            value={config.parameters || {}}
            onChange={(parameters) => onChange({ parameters })}
            placeholder='{\n  "name": "value"\n}'
            rows={8}
          />
          <span className="form-hint">
            JSON object passed as script parameter via Get(ScriptParameter).
            <br />
            <strong>FileMaker Pattern:</strong> Use <code>{'JsonSetElement ( "{}" ; "name" ; "value" ; 1 )'}</code> to build parameters
          </span>
        </div>
      </div>

      <div className="config-section">
        <h4>Result Extraction</h4>
        
        <div className="form-group">
          <label>Result Path (JSONPath)</label>
          <input
            type="text"
            value={config.resultPath || '$.JSON'}
            onChange={(e) => onChange({ resultPath: e.target.value })}
            placeholder="$.JSON"
          />
          <span className="form-hint">
            JSONPath to extract data from script result. <strong>Default: $.JSON</strong>
            <br />
            Your script should return: <code>{'Exit Script [ Text Result: JSONSetElement ( "{}" ; "JSON" ; $data ; 3 ) ]'}</code>
            <br />
            Other examples: <code>$.data</code>, <code>$.records[*]</code>, <code>$..items</code>
          </span>
        </div>

        <div className="info-box" style={{ 
          padding: '1rem', 
          background: 'var(--surface-raised)', 
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          marginTop: '1rem'
        }}>
          <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>
            💡 Script Result Format (Default)
          </h5>
          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            By default, your script should return a JSON object with a <strong>JSON</strong> key containing the data:
            <br />
            <code style={{ display: 'block', marginTop: '0.5rem', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
              {'Exit Script [ Text Result: JsonSetElement ( "{}" ; "JSON" ; $yourData ; 3 ) ]'}
            </code>
          </p>
        </div>
      </div>

      <div className="config-section">
        <h4>FileMaker SQL Query Helper</h4>
        <p className="form-hint" style={{ marginBottom: '1rem' }}>
          Since this tool runs in a FileMaker WebViewer, you can use FileMaker scripts
          to query database schema via ExecuteSQL and pass results back.
        </p>
        
        <div className="info-box" style={{ 
          padding: '1rem', 
          background: 'var(--surface-raised)', 
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)'
        }}>
          <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>
            📋 Suggested Script Pattern
          </h5>
          <pre style={{ 
            margin: 0, 
            fontSize: '0.75rem', 
            color: 'var(--text-secondary)',
            whiteSpace: 'pre-wrap'
          }}>
{`// In FileMaker Script:
Set Variable [ $params ; Get(ScriptParameter) ]
Set Variable [ $query ; JSONGetElement($params; "query") ]

// Execute SQL to get data
Set Variable [ $result ; ExecuteSQL($query; ""; "") ]

// Format as JSON array
Set Variable [ $json ; /* convert to JSON */ ]

Exit Script [ Text Result: $json ]`}
          </pre>
        </div>
      </div>

      <div className="config-actions">
        <button 
          onClick={handleTest}
          disabled={isTesting || !config.scriptName}
          className="btn btn-secondary"
        >
          {isTesting ? '⟳ Testing...' : '▶️ Test Script'}
        </button>

        {testResult && (
          <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
            <span className="result-icon">
              {testResult.success ? '✓' : '✗'}
            </span>
            <span className="result-message">{testResult.message}</span>
          </div>
        )}
      </div>

      {testResult?.data && (
        <div className="config-section">
          <h4>Test Result Preview</h4>
          <pre className="config-preview">
            {JSON.stringify(testResult.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
